import { DEFAULT_ASPECT_RATIO } from "./types.ts";
import { createId, nowIso } from "./ids.ts";
import type {
  AlbumDocument,
  AlbumPage,
  AppSettings,
  Placement,
  RoomFurnitureLayout,
  RoomImageAssets,
} from "./types.ts";
import { nextLayer, normalizeRect, clamp } from "./geometry.ts";
import {
  DEFAULT_ROOM_FURNITURE_LAYOUT,
  normalizeRoomFurnitureLayout,
} from "../room/furniture.ts";

type LegacyAppSettings = Omit<AppSettings, "roomImages" | "roomFurnitureLayout"> & {
  roomImages?: Partial<RoomImageAssets> | null;
  roomFurnitureLayout?: Partial<RoomFurnitureLayout> | null;
};

function emptyRoomImages(): RoomImageAssets {
  return {
    room: null,
    shelf: null,
    fireplace: null,
    table: null,
  };
}

export function createDefaultSettings(deviceId: string): AppSettings {
  return {
    id: "settings",
    theme: "system",
    reducedMotion: false,
    syncEnabled: false,
    lastSyncAt: null,
    deviceId,
    roomImages: emptyRoomImages(),
    roomFurnitureLayout: structuredClone(DEFAULT_ROOM_FURNITURE_LAYOUT),
  };
}

/** Adds defaults introduced after the settings object was first persisted. */
export function normalizeSettings(settings: LegacyAppSettings): AppSettings {
  return {
    ...settings,
    roomImages: {
      room: settings.roomImages?.room ?? null,
      shelf: settings.roomImages?.shelf ?? null,
      fireplace: settings.roomImages?.fireplace ?? null,
      table: settings.roomImages?.table ?? null,
    },
    roomFurnitureLayout: normalizeRoomFurnitureLayout(settings.roomFurnitureLayout),
  };
}

export function createEmptyPage(): AlbumPage {
  return { id: createId(), placements: [] };
}

export function createAlbum(title = "Untitled Album"): AlbumDocument {
  const now = nowIso();
  return {
    id: createId(),
    title,
    coverAssetId: null,
    aspectRatio: DEFAULT_ASPECT_RATIO,
    revision: 1,
    baseRevision: null,
    pages: [createEmptyPage(), createEmptyPage()],
    createdAt: now,
    updatedAt: now,
    conflictCopyOf: null,
    syncedRevision: null,
  };
}

export function bumpRevision(album: AlbumDocument): AlbumDocument {
  return {
    ...album,
    revision: album.revision + 1,
    updatedAt: nowIso(),
  };
}

export function addPlacement(
  album: AlbumDocument,
  pageId: string,
  assetId: string,
  partial?: Partial<Placement>,
): AlbumDocument {
  const pages = album.pages.map((page) => {
    if (page.id !== pageId) return page;
    const placement: Placement = {
      id: createId(),
      assetId,
      x: partial?.x ?? 0.1,
      y: partial?.y ?? 0.1,
      width: partial?.width ?? 0.35,
      height: partial?.height ?? 0.35,
      rotation: partial?.rotation ?? 0,
      layer: partial?.layer ?? nextLayer(page.placements),
    };
    return { ...page, placements: [...page.placements, placement] };
  });
  return bumpRevision({ ...album, pages });
}

export function updatePlacement(
  album: AlbumDocument,
  pageId: string,
  placementId: string,
  patch: Partial<Placement>,
): AlbumDocument {
  const pages = album.pages.map((page) => {
    if (page.id !== pageId) return page;
    const placements = page.placements.map((p) => {
      if (p.id !== placementId) return p;
      const merged = { ...p, ...patch };
      const normalized = normalizeRect({
        x: merged.x,
        y: merged.y,
        width: merged.width,
        height: merged.height,
      });
      return {
        ...merged,
        ...normalized,
        rotation: clamp(merged.rotation, -Math.PI, Math.PI),
      };
    });
    return { ...page, placements };
  });
  return bumpRevision({ ...album, pages });
}

export function removePlacement(
  album: AlbumDocument,
  pageId: string,
  placementId: string,
): AlbumDocument {
  const pages = album.pages.map((page) => {
    if (page.id !== pageId) return page;
    return {
      ...page,
      placements: page.placements.filter((p) => p.id !== placementId),
    };
  });
  return bumpRevision({ ...album, pages });
}

export function addPage(album: AlbumDocument): AlbumDocument {
  return bumpRevision({
    ...album,
    pages: [...album.pages, createEmptyPage()],
  });
}

export function removePage(album: AlbumDocument, pageId: string): AlbumDocument {
  if (album.pages.length <= 1) return album;
  return bumpRevision({
    ...album,
    pages: album.pages.filter((p) => p.id !== pageId),
  });
}

export function reorderPage(
  album: AlbumDocument,
  pageId: string,
  toIndex: number,
): AlbumDocument {
  const pages = [...album.pages];
  const fromIndex = pages.findIndex((p) => p.id === pageId);
  if (fromIndex < 0) return album;
  const [page] = pages.splice(fromIndex, 1);
  if (!page) return album;
  pages.splice(toIndex, 0, page);
  return bumpRevision({ ...album, pages });
}

export function setCover(album: AlbumDocument, assetId: string | null): AlbumDocument {
  return bumpRevision({ ...album, coverAssetId: assetId });
}

export function renameAlbum(album: AlbumDocument, title: string): AlbumDocument {
  return bumpRevision({ ...album, title: title.trim() || "Untitled Album" });
}

export function changeLayer(
  album: AlbumDocument,
  pageId: string,
  placementId: string,
  delta: number,
): AlbumDocument {
  const pages = album.pages.map((page) => {
    if (page.id !== pageId) return page;
    const sorted = [...page.placements].sort((a, b) => a.layer - b.layer);
    const index = sorted.findIndex((p) => p.id === placementId);
    if (index < 0) return page;
    const target = index + delta;
    if (target < 0 || target >= sorted.length) return page;
    const next = [...sorted];
    const [item] = next.splice(index, 1);
    if (!item) return page;
    next.splice(target, 0, item);
    const layers = next.map((p, i) => ({ ...p, layer: i }));
    return { ...page, placements: layers };
  });
  return bumpRevision({ ...album, pages });
}
